/**
 * AmbientCG PBR materials for VR: list a page, and apply one to a shape
 * (download the 1K zip through our proxy, upload its maps, set pbrMaps).
 * Shared by the wrist palette's Materials panel.
 */
import { useCallback, useEffect, useState } from 'react';
import JSZip from 'jszip';
import { useWorldStore, type PbrMaps } from '../../../stores/worldStore';
import { putObject } from '../../../utils/uploadToStorage';
import { broadcastObjectUpdate } from '../../../services/worldBroadcast';

// Production uses same-origin functions (/api/ambientcg/*); dev falls back to the local proxy.
const BASE = import.meta.env.PROD ? '' : (import.meta.env.VITE_SOCKET_URL || `http://localhost:${import.meta.env.VITE_SERVER_PORT || '8349'}`);

export interface MaterialAsset { id: string; name: string; thumbnailUrl: string; downloadUrl: string }

const MAP_KEYWORDS: Record<string, string[]> = {
  color: ['color', 'diffuse', 'albedo', 'basecolor', 'col', 'diff'],
  normal: ['normal', 'norm', 'nrm', 'normalgl'],
  roughness: ['roughness', 'rough', 'rgh'],
  ao: ['ao', 'ambientocclusion', 'occlusion'],
};
const findMap = (files: string[], keywords: string[]) => files.find((name) => {
  const suffix = (name.split('/').pop()?.split('.')[0] || '').split(/[_-]/).pop()?.toLowerCase() || '';
  return keywords.some((kw) => suffix === kw || suffix.includes(kw));
});

export function useAmbientCG(open: boolean, perPage: number) {
  const [assets, setAssets] = useState<MaterialAsset[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setError(null);
    fetch(`${BASE}/api/ambientcg/assets?type=material&limit=${perPage}&offset=${page * perPage}&sort=popular&include=downloads,thumbnails`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        if (!alive) return;
        setTotal(data.totalResults || 0);
        setAssets((data.assets || []).map((a: any) => {
          const dl = a.downloads?.find((d: any) => d.attributes === '1K-JPG') || a.downloads?.[0];
          return {
            id: a.id,
            name: a.id.replace(/([A-Z])/g, ' $1').replace(/(\d+)/g, ' $1').trim().slice(0, 14),
            thumbnailUrl: a.thumbnails?.['256-JPG-242424'] || a.thumbnails?.['128-PNG'] || '',
            downloadUrl: dl?.url || '',
          };
        }));
      })
      .catch((e) => { if (alive) setError(e.message || 'Couldn’t load materials'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, page, perPage]);

  const pages = Math.max(1, Math.ceil(total / perPage));
  return { assets, page, pages, setPage, loading, error };
}

/** Apply a material to a shape. Resolves false if it couldn't be applied. */
export async function applyAmbientCG(worldId: string, primId: string, asset: MaterialAsset, tile: number): Promise<boolean> {
  if (!asset.downloadUrl) return false;
  const res = await fetch(`${BASE}/api/ambientcg/download?url=${encodeURIComponent(asset.downloadUrl)}`);
  if (!res.ok) return false;
  const zip = await JSZip.loadAsync(await res.blob());
  const files = Object.keys(zip.files).filter((n) => /\.(jpe?g|png)$/i.test(n) && !n.startsWith('__MACOSX'));
  const upload = async (fileName: string) => {
    const data = await zip.files[fileName].async('blob');
    const ext = fileName.split('.').pop() || 'jpg';
    const key = `textures/acg-${Date.now()}-${fileName.split('/').pop()}`;
    return putObject(key, data, `image/${ext.toLowerCase() === 'jpg' ? 'jpeg' : ext.toLowerCase()}`);
  };
  const pbrMaps: PbrMaps = { colorUrl: '' };
  const c = findMap(files, MAP_KEYWORDS.color); if (c) pbrMaps.colorUrl = await upload(c);
  const n = findMap(files, MAP_KEYWORDS.normal); if (n) pbrMaps.normalUrl = await upload(n);
  const r = findMap(files, MAP_KEYWORDS.roughness); if (r) pbrMaps.roughnessUrl = await upload(r);
  const a = findMap(files, MAP_KEYWORDS.ao); if (a) pbrMaps.aoUrl = await upload(a);
  if (!pbrMaps.colorUrl) return false;
  useWorldStore.getState().updatePrimitive(primId, { pbrMaps, texturePreset: null, textureUrl: null, textureScale: tile });
  if (worldId) broadcastObjectUpdate(worldId, 'primitive', primId, { pbrMaps, texturePreset: null, textureUrl: null, textureScale: tile });
  return true;
}
